import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import ConfiguracionFinanciera from './ConfiguracionFinanciera';
import GestionGastos from './Gestiongastos';
import GestionEmpleados from './Gestionempleados';
import GestionSubcontratistas from './Gestionsubcontratistas';
import ReportesFinancieros from './Reportesfinancieros';
import CargaRapidaNomina from './CargaRapidaNomina';

const DashboardFinanciero = () => {
  const { proyectoId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [proyecto, setProyecto] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [mostrarConfiguracion, setMostrarConfiguracion] = useState(false);
  const [vistaActual, setVistaActual] = useState('dashboard'); 
  const [modoNomina, setModoNomina] = useState('asistencia');

  useEffect(() => {
    cargarDatos();
  }, [proyectoId]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [proyectoRes, dashboardRes] = await Promise.all([
        api.get(`/proyectos/${proyectoId}`),
        api.get(`/proyectos/${proyectoId}/financiero/dashboard`)
      ]);
      
      setProyecto(proyectoRes.data);
      setDashboard(dashboardRes.data);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      alert('❌ Error al cargar información financiera');
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (valor) => {
    return `RD$ ${parseFloat(valor || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>Cargando información financiera...</p>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div style={styles.error}>
        <h2>❌ Error</h2>
        <p>No se pudo cargar la información financiera</p>
        <button onClick={() => navigate('/proyectos')} style={styles.btnVolver}>
          ← Volver a Proyectos
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header Principal */}
      <div style={styles.header}>
        <div>
          <button onClick={() => navigate('/proyectos')} style={styles.btnBack}>
            ← Volver
          </button>
          <h1 style={styles.title}>💼 Control Financiero</h1>
          <h2 style={styles.subtitle}>{proyecto?.nombre}</h2>
        </div>
        <button 
          onClick={() => setMostrarConfiguracion(true)} 
          style={styles.btnConfig}
        >
          ⚙️ Configuración
        </button>
      </div>

      {/* Navegación por Pestañas */}
      <div style={styles.tabs}>
        <button style={{...styles.tab, ...(vistaActual === 'dashboard' ? styles.tabActive : {})}} onClick={() => setVistaActual('dashboard')}>📊 Dashboard</button>
        <button style={{...styles.tab, ...(vistaActual === 'gastos' ? styles.tabActive : {})}} onClick={() => setVistaActual('gastos')}>💰 Gastos</button>
        <button style={{...styles.tab, ...(vistaActual === 'empleados' ? styles.tabActive : {})}} onClick={() => setVistaActual('empleados')}>👥 Empleados</button>
        <button style={{...styles.tab, ...(vistaActual === 'subcontratistas' ? styles.tabActive : {})}} onClick={() => setVistaActual('subcontratistas')}>🔧 Subcontratistas</button>
        <button style={{...styles.tab, ...(vistaActual === 'reportes' ? styles.tabActive : {})}} onClick={() => setVistaActual('reportes')}>📊 Reportes</button>
      </div>

      {/* VISTA: DASHBOARD */}
      {vistaActual === 'dashboard' && (
        <>
          <div style={styles.cardsGrid}>
            <div style={{...styles.card, borderLeft: '4px solid #3b82f6'}}>
              <div style={styles.cardIcon}>💰</div>
              <div>
                <p style={styles.cardLabel}>Valor del Contrato</p>
                <h3 style={styles.cardValue}>{formatMoney(dashboard.proyecto.valor_contrato)}</h3>
              </div>
            </div>

            <div style={{...styles.card, borderLeft: '4px solid #ef4444'}}>
              <div style={styles.cardIcon}>💸</div>
              <div>
                <p style={styles.cardLabel}>Total Gastado</p>
                <h3 style={styles.cardValue}>{formatMoney(dashboard.totales.gastado)}</h3>
                <p style={styles.cardPercent}>{dashboard.totales.porcentaje_gastado}% del presupuesto</p>
              </div>
            </div>

            <div style={{...styles.card, borderLeft: '4px solid #10b981'}}>
              <div style={styles.cardIcon}>💵</div>
              <div>
                <p style={styles.cardLabel}>Disponible</p>
                <h3 style={styles.cardValue}>{formatMoney(dashboard.totales.disponible)}</h3>
              </div>
            </div>

            <div style={{...styles.card, borderLeft: '4px solid #8b5cf6'}}>
              <div style={styles.cardIcon}>📈</div>
              <div>
                <p style={styles.cardLabel}>Ganancia Proyectada</p>
                <h3 style={{...styles.cardValue, color: '#059669'}}>{formatMoney(dashboard.rentabilidad.ganancia_estimada)}</h3>
                <p style={styles.cardPercent}>Margen: {((dashboard.rentabilidad.ganancia_estimada / dashboard.proyecto.valor_contrato) * 100).toFixed(2)}%</p>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>💼 Desglose de Costos</h2>
            <div style={styles.desglose}>
              <div style={styles.costoCard}>
                <h3 style={styles.costoTitle}>Costos Directos</h3>
                <div style={styles.costoItem}><span>📦 Materiales</span><span>{formatMoney(dashboard.costos_directos.materiales)}</span></div>
                <div style={styles.costoItem}><span>👷 Mano de Obra</span><span>{formatMoney(dashboard.costos_directos.mano_obra)}</span></div>
                <div style={styles.costoItem}><span>🔧 Subcontratistas</span><span>{formatMoney(dashboard.costos_directos.subcontratistas)}</span></div>
                <div style={{...styles.costoItem, borderTop: '2px solid #e2e8f0', marginTop: '10px', paddingTop: '10px'}}>
                    <span style={{fontWeight: '700'}}>Subtotal</span>
                    <span style={{fontWeight: '700', fontSize: '18px'}}>{formatMoney(dashboard.costos_directos.subtotal)}</span>
                </div>
              </div>
              <div style={styles.costoCard}>
                <h3 style={styles.costoTitle}>Costos Indirectos</h3>
                <div style={styles.costoItem}><span>💰 Gastos Varios</span><span>{formatMoney(dashboard.costos_indirectos.gastos)}</span></div>
                <div style={{...styles.costoItem, borderTop: '2px solid #e2e8f0', marginTop: '10px', paddingTop: '10px'}}>
                    <span style={{fontWeight: '700'}}>Subtotal</span>
                    <span style={{fontWeight: '700', fontSize: '18px'}}>{formatMoney(dashboard.costos_indirectos.subtotal)}</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* VISTA: GASTOS */}
      {vistaActual === 'gastos' && (
        <GestionGastos proyectoId={proyectoId} proyecto={proyecto} />
      )}

      {/* VISTA: EMPLEADOS (CORREGIDA ALINEACIÓN) */}
      {vistaActual === 'empleados' && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={{...styles.sectionTitle, margin: 0}}> </h2>
            <button 
              onClick={() => setModoNomina(modoNomina === 'asistencia' ? 'rapida' : 'asistencia')}
              style={styles.btnPrimary}
            >
              {modoNomina === 'asistencia' ? '🚀 Modo Carga Rápida' : '📅 Modo Asistencia Diaria'}
            </button>
          </div>

          {modoNomina === 'asistencia' ? (
            <GestionEmpleados proyectoId={proyectoId} proyecto={proyecto} />
          ) : (
            <CargaRapidaNomina 
              empleadosProyecto={dashboard.empleados_lista || []} 
              proyectoId={proyectoId}
              onComplete={() => {
                setModoNomina('asistencia');
                cargarDatos();
              }}
            />
          )}
        </div>
      )}

      {/* OTRAS VISTAS */}
      {vistaActual === 'subcontratistas' && <GestionSubcontratistas proyectoId={proyectoId} proyecto={proyecto} />}
      {vistaActual === 'reportes' && <ReportesFinancieros proyectoId={proyectoId} proyecto={proyecto} />}

      {/* Modal de Configuración */}
      {mostrarConfiguracion && (
        <div style={styles.modalOverlay}>
          <ConfiguracionFinanciera
            proyectoId={proyectoId}
            onClose={() => setMostrarConfiguracion(false)}
            onActualizar={() => { cargarDatos(); setMostrarConfiguracion(false); }}
          />
        </div>
      )}
    </div>
  );
};

// ESTILOS CORREGIDOS PARA ALINEACIÓN PERFECTA
const styles = {
  container: { minHeight: '100vh', background: '#f8fafc', padding: '20px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px' },
  title: { fontSize: '32px', fontWeight: '700', color: '#1e293b', margin: '0 0 5px 0' },
  subtitle: { fontSize: '18px', color: '#64748b', margin: 0, fontWeight: '400' },
  tabs: { display: 'flex', gap: '10px', marginBottom: '30px', borderBottom: '2px solid #e2e8f0' },
  tab: { padding: '12px 24px', background: 'none', border: 'none', borderBottom: '3px solid transparent', cursor: 'pointer', fontSize: '15px', fontWeight: '600', color: '#64748b' },
  tabActive: { color: '#8b5cf6', borderBottom: '3px solid #8b5cf6' },
  cardsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' },
  card: { background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', gap: '15px', alignItems: 'flex-start' },
  cardIcon: { fontSize: '36px' },
  cardLabel: { fontSize: '13px', color: '#64748b', margin: '0 0 8px 0', fontWeight: '600' },
  cardValue: { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 },
  cardPercent: { fontSize: '12px', color: '#64748b', marginTop: '5px' },
  section: { background: 'white', padding: '30px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  sectionHeader: { 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center', // Alineación vertical perfecta
    marginBottom: '25px' 
  },
  sectionTitle: { fontSize: '20px', fontWeight: '700', color: '#1e293b', margin: 0 }, // Sin margen para no desalinear
  btnPrimary: { background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  desglose: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' },
  costoCard: { background: '#f8fafc', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0' },
  costoTitle: { fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '15px' },
  costoItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' },
  btnConfig: { background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }
};

export default DashboardFinanciero;